import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-light-svg-icons/faChevronLeft';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { OutreachShell } from '../components/OutreachShell';
import { OutreachPlanCard } from '../components/OutreachPlanCard';
import { PlugBar } from '../components/PlugBar';
import { useInbox, InboxFilters } from '../hooks/useInbox';
import { useOutreachPlan } from '../hooks/useOutreachPlan';
import { outreachReplyPath } from '../outreachPaths';
import { plugMessages } from '../plugMessages';
import { InboxCampaignPicker } from './InboxCampaignPicker';
import { InboxList } from './InboxList';
import { InboxEmpty } from './InboxEmpty';
import { ReplyThread } from './ReplyThread';
import { inboxMessages as messages } from './inboxMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Outreach area; each part uses its own slice of it */
import styles from './Inbox.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface InboxScreenProps {
  accountId: number;
  replyUuid: string | null;
  initialCampaign: string | null;
}

/**
 * The inbox (design outreach-v2 m1 to m4): the Plug bar with the sorted replies, the list in groups
 * (Needs you, Added your song, Done, Hidden), the open thread and its side panel. On a narrow screen
 * the list or the thread shows (m4). `outreach.reply.received` reads the list again.
 */
const InboxScreenComponent: React.FC<InboxScreenProps> = ({ accountId, replyUuid, initialCampaign }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const router = useSMLRouter();
  const plan = useOutreachPlan();
  const [filters, setFilters] = useState<InboxFilters>({ campaignUuid: initialCampaign });
  const inbox = useInbox(accountId, filters);
  const open = useCallback(
    (uuid: string) => {
      // eslint-disable-next-line no-void
      void navigate(outreachReplyPath(uuid));
    },
    [navigate]
  );

  const content = () => {
    if (inbox.isLoading) {
      return <LoadingView />;
    }
    if (inbox.error !== null) {
      return <ErrorView error={inbox.error ?? undefined} retry={inbox.retry} />;
    }
    if (inbox.rows.length === 0 && !inbox.hasMore) {
      return <InboxEmpty campaignUuid={filters.campaignUuid} />;
    }
    return (
      <div className={styles.layout}>
        <div className={replyUuid === null ? undefined : styles.hideNarrow}>
          <InboxList
            rows={inbox.rows}
            counts={inbox.counts}
            campaignFiltered={filters.campaignUuid !== null}
            openUuid={replyUuid}
            onOpen={open}
            hasMore={inbox.hasMore}
            onMore={() => inbox.loadMore().catch(console.error)}
          />
        </div>
        <div className={replyUuid === null ? styles.hideNarrow : undefined}>
          <button type="button" className={styles.backNarrow} onClick={() => router.push(SMLRoutes.outreachInbox)}>
            <FontAwesomeIcon icon={faChevronLeft} />
            {intl.formatMessage(messages.back)}
          </button>
          <ReplyThread accountId={accountId} replyUuid={replyUuid} />
        </div>
      </div>
    );
  };

  return (
    <OutreachShell
      section="item"
      title={intl.formatMessage(messages.title)}
      subtitle={intl.formatMessage(messages.subtitle)}
      actions={
        <InboxCampaignPicker
          accountId={accountId}
          value={filters.campaignUuid}
          onChange={(campaignUuid) => setFilters((current) => ({ ...current, campaignUuid }))}
        />
      }
    >
      {!plan.canPitch && <OutreachPlanCard />}
      {inbox.counts !== null && inbox.counts.all > 0 && (
        <PlugBar
          title={intl.formatMessage(plugMessages.sorted, { count: inbox.counts.all, needs: inbox.counts.needsYou })}
          sub={intl.formatMessage(plugMessages.sortedSub)}
        />
      )}
      {content()}
    </OutreachShell>
  );
};

export const InboxScreen = TypedMemo(InboxScreenComponent);
